import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, X, FileText, ImageIcon, Video, AlertTriangle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const humanSize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const FileIcon = ({ f }) => {
  if (f.is_image || f.content_type?.startsWith("image/")) return <ImageIcon size={15}/>;
  if (f.is_video || f.content_type?.startsWith("video/")) return <Video size={15}/>;
  return <FileText size={15}/>;
};

const LARGE_SCOPES = ["artwork", "design_draft"];

export default function FileDropzone({
  scope = "misc",
  jobId,              // optional — pass through as ?job_id= for scope=artwork ownership
  briefId,            // optional — pass through as ?brief_id= for scope=design_draft ownership
  accept,             // string, e.g. "image/*,video/*" or "application/pdf"
  multiple = true,
  value = [],         // uploaded records
  onChange,           // (records[]) => void
  label = "Drop files here or click to browse",
  hint,
  testid = "file-dropzone",
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState([]);   // [{name, progress}]
  const [error, setError] = useState("");

  const uploadOne = useCallback(async (file) => {
    setUploading((u) => [...u, { name: file.name, progress: 0 }]);
    try {
      const maxMB = LARGE_SCOPES.includes(scope) ? 100 : 10;
      if (file.size > maxMB * 1024 * 1024) throw { response: { data: { detail: `${file.name} is larger than ${maxMB} MB` } } };
      const setPct = (pct) => setUploading((u) => u.map((x) => (x.name === file.name ? { ...x, progress: pct } : x)));
      let data;
      if (file.size > 8 * 1024 * 1024) {
        const init = (await axios.post(`${API}/files/chunked/init`, {
          filename: file.name, size: file.size, content_type: file.type || null, scope, job_id: jobId || null, brief_id: briefId || null,
        })).data;
        for (let i = 0; i < init.total_chunks; i++) {
          const part = new FormData();
          part.append("chunk", file.slice(i * init.chunk_size, (i + 1) * init.chunk_size), file.name);
          await axios.put(`${API}/files/chunked/${init.upload_id}/${i}`, part);
          setPct(Math.round(((i + 1) / init.total_chunks) * 95));
        }
        data = (await axios.post(`${API}/files/chunked/${init.upload_id}/complete`)).data;
      } else {
        const form = new FormData();
        form.append("file", file);
        const qs = new URLSearchParams({ scope });
        if (jobId) qs.set("job_id", jobId);
        if (briefId) qs.set("brief_id", briefId);
        data = (await axios.post(`${API}/files/upload?${qs.toString()}`, form, {
          onUploadProgress: (evt) => setPct(Math.round((evt.loaded / (evt.total || evt.loaded)) * 100)),
        })).data;
      }
      setUploading((u) => u.filter((x) => x.name !== file.name));
      return data;
    } catch (e) {
      setUploading((u) => u.filter((x) => x.name !== file.name));
      setError(e.response?.data?.detail || `Upload failed for ${file.name}`);
      return null;
    }
  }, [scope, jobId, briefId]);

  const handleFiles = useCallback(async (files) => {
    setError("");
    const arr = Array.from(files || []);
    const results = [];
    for (const f of arr) {
      const rec = await uploadOne(f);
      if (rec) results.push(rec);
    }
    if (results.length) onChange?.(multiple ? [...value, ...results] : results);
  }, [uploadOne, onChange, value, multiple]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeAt = (i) => onChange?.(value.filter((_, idx) => idx !== i));

  return (
    <div className="dropzone-wrap" data-testid={testid}>
      <div
        role="button"
        tabIndex={0}
        className={`dropzone ${dragging ? "dragging" : ""}`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        data-testid={`${testid}-drop-area`}
      >
        <UploadCloud size={26}/>
        <b>{label}</b>
        <span>{hint || `PDF, images, or video — up to ${LARGE_SCOPES.includes(scope) ? 100 : 10} MB each`}</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testid}-input`}
      />

      {uploading.length > 0 && (
        <div className="dz-upload-list">
          {uploading.map((u) => (
            <div className="dz-uploading" key={u.name}>
              <span>{u.name}</span>
              <div className="dz-progress"><div style={{ width: `${u.progress}%` }}/></div>
              <b>{u.progress}%</b>
            </div>
          ))}
        </div>
      )}

      {value.length > 0 && (
        <div className="dz-file-list">
          {value.map((f, i) => {
            const pf = f.preflight;
            const sev = pf?.severity || "ok";
            const sevStyle = {
              ok:   { bg:"#ECFDF5", bd:"#A7F3D0", fg:"#065F46", icon:<CheckCircle2 size={12}/> },
              warn: { bg:"#FFFBEB", bd:"#FDE68A", fg:"#92400E", icon:<AlertTriangle size={12}/> },
              fail: { bg:"#FEF2F2", bd:"#FCA5A5", fg:"#991B1B", icon:<AlertTriangle size={12}/> },
            }[sev] || null;
            const fails = (pf?.checks || []).filter(c => !c.ok);
            return (
              <div className="dz-file" key={f.id || i} data-testid={`${testid}-file-${i}`} style={{flexDirection:"column", alignItems:"stretch"}}>
                <div style={{display:"flex", alignItems:"center", gap:10, width:"100%"}}>
                  <div className="dz-icon"><FileIcon f={f}/></div>
                  <div className="dz-info" style={{flex:1, minWidth:0}}>
                    <b>{f.name || f.original_filename || "file"}</b>
                    <span>{humanSize(f.size)} · {f.content_type}</span>
                  </div>
                  {pf && sevStyle && (
                    <span
                      data-testid={`${testid}-preflight-${i}`}
                      data-severity={sev}
                      style={{
                        display:"inline-flex", alignItems:"center", gap:4,
                        background: sevStyle.bg, border:`1px solid ${sevStyle.bd}`,
                        color: sevStyle.fg, padding:"2px 8px", borderRadius:999,
                        fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:0.3,
                        whiteSpace:"nowrap",
                      }}
                      title={sev === "ok" ? "Preflight passed" : `${fails.length} finding${fails.length === 1 ? "" : "s"}`}
                    >
                      {sevStyle.icon}
                      {sev === "ok" ? "Preflight ok" : sev === "fail" ? "Preflight fail" : "Preflight warn"}
                    </span>
                  )}
                  <button type="button" className="dz-remove" onClick={() => removeAt(i)} data-testid={`${testid}-remove-${i}`}>
                    <X size={14}/>
                  </button>
                </div>
                {fails.length > 0 && (
                  <ul
                    data-testid={`${testid}-preflight-findings-${i}`}
                    style={{
                      margin:"8px 0 0 32px", padding:"6px 10px", listStyle:"disc",
                      background: sevStyle.bg, border:`1px solid ${sevStyle.bd}`,
                      borderRadius:6, fontSize:11, color: sevStyle.fg, lineHeight:1.5,
                    }}
                  >
                    {fails.map((c, j) => (<li key={j}>{c.message}</li>))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error && <div className="form-error" data-testid={`${testid}-error`}>{error}</div>}
    </div>
  );
}
